import { useRef, useState } from 'react'
import styled from 'styled-components'
import { ChainId } from '@etcswapv2/sdk-legacy'
import { ChevronDown } from 'react-feather'
import { useActiveWeb3React } from '../../hooks'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'

// Chain logos from ETCswap tokens repo
const ETC_LOGO = 'https://raw.githubusercontent.com/etcswap/tokens/a39a3b80b18add68c053ae2c336d563b41d8cea7/ethereum-classic/logos/ethereum-classic-etc-logo.svg'
const MORDOR_LOGO = 'https://raw.githubusercontent.com/etcswap/tokens/a39a3b80b18add68c053ae2c336d563b41d8cea7/ethereum-classic/logos/mordor-testnet-metc-logo.svg'

const SelectorWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SelectorButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme, $isOpen }) => ($isOpen ? theme.green1 : theme.bg3)};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.green1};
  }
`

const NetworkIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`

const NetworkName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
`

const ChevronWrapper = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.text2};
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  z-index: 100;
`

const NetworkOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  background: ${({ $active, theme }) => ($active ? theme.bg2 : 'transparent')};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.bg2};
  }
`

const NetworkInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const NetworkLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
`

const NetworkStatus = styled.span<{ $isTestnet?: boolean }>`
  font-size: 11px;
  color: ${({ theme, $isTestnet }) => ($isTestnet ? theme.orange1 : theme.green1)};
`

const CheckMark = styled.div`
  margin-left: auto;
  color: ${({ theme }) => theme.green1};
  font-size: 16px;
`

interface NetworkConfig {
  chainId: ChainId
  name: string
  icon: string
  isTestnet: boolean
  rpcUrl: string
}

const NETWORKS: NetworkConfig[] = [
  {
    chainId: ChainId.CLASSIC,
    name: 'Ethereum Classic',
    icon: ETC_LOGO,
    isTestnet: false,
    rpcUrl: 'https://etc.rivet.link'
  },
  {
    chainId: ChainId.MORDOR,
    name: 'Mordor Testnet',
    icon: MORDOR_LOGO,
    isTestnet: true,
    rpcUrl: 'https://rpc.mordor.etccooperative.org'
  }
]

export default function NetworkSelector() {
  const { chainId } = useActiveWeb3React()
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useOnClickOutside(ref, () => setIsOpen(false))

  const currentNetwork = NETWORKS.find((n) => n.chainId === chainId) ?? NETWORKS[0]

  const handleNetworkSwitch = async (network: NetworkConfig) => {
    if (network.chainId === chainId) {
      setIsOpen(false)
      return
    }

    const ethereum = window.ethereum as { request?: (args: { method: string; params?: unknown[] }) => Promise<unknown> }
    if (!ethereum?.request) {
      setIsOpen(false)
      return
    }

    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${network.chainId.toString(16)}` }]
      })
    } catch (switchError: unknown) {
      // Chain not added to wallet, try adding it
      const error = switchError as { code?: number }
      if (error.code === 4902) {
        try {
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${network.chainId.toString(16)}`,
                chainName: network.name,
                nativeCurrency: {
                  name: network.isTestnet ? 'Mordor ETC' : 'Ethereum Classic',
                  symbol: network.isTestnet ? 'METC' : 'ETC',
                  decimals: 18
                },
                rpcUrls: [network.rpcUrl],
                blockExplorerUrls: [
                  network.isTestnet ? 'https://etc-mordor.blockscout.com' : 'https://etc.blockscout.com'
                ]
              }
            ]
          })
        } catch (addError) {
          console.error('Failed to add network:', addError)
        }
      }
    }
    setIsOpen(false)
  }

  return (
    <SelectorWrapper ref={ref}>
      <SelectorButton $isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <NetworkIcon src={currentNetwork.icon} alt={currentNetwork.name} />
        <NetworkName>{currentNetwork.isTestnet ? 'Mordor' : 'ETC'}</NetworkName>
        <ChevronWrapper $isOpen={isOpen}>
          <ChevronDown size={16} />
        </ChevronWrapper>
      </SelectorButton>

      {isOpen && (
        <Dropdown>
          {NETWORKS.map((network) => (
            <NetworkOption
              key={network.chainId}
              $active={network.chainId === chainId}
              onClick={() => handleNetworkSwitch(network)}
            >
              <NetworkIcon src={network.icon} alt={network.name} />
              <NetworkInfo>
                <NetworkLabel>{network.name}</NetworkLabel>
                {network.isTestnet && <NetworkStatus $isTestnet>Testnet</NetworkStatus>}
              </NetworkInfo>
              {network.chainId === chainId && <CheckMark>✓</CheckMark>}
            </NetworkOption>
          ))}
        </Dropdown>
      )}
    </SelectorWrapper>
  )
}
